'use client';

/**
 * @fileoverview Renders a single chat message — user turn, assistant
 * turn, or the in-flight streaming assistant message with live
 * tool-call pills.
 */

import type { ChatMessage, ChatToolCall } from '@/domains/chat/schemas';
import { cn } from '@/lib/utils';
import { ToolCallPill } from './tool-call-pill';

interface MessageBubbleProps {
  /** Author role. Only 'user' and 'assistant' render; tool/system are filtered upstream. */
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ChatToolCall[];
  /** If true, the content is still streaming — render a trailing caret. */
  isStreaming?: boolean;
}

export function MessageBubble({
  role,
  content,
  toolCalls,
  isStreaming,
}: MessageBubbleProps) {
  const isUser = role === 'user';

  return (
    <div
      className={cn(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'max-w-[90%] space-y-2 rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm',
        )}
      >
        {toolCalls && toolCalls.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {toolCalls.map((tc) => (
              <ToolCallPill key={tc.id} toolCall={tc} />
            ))}
          </div>
        )}

        {content && (
          <div className="whitespace-pre-wrap break-words">
            {content}
            {isStreaming && (
              <span
                aria-hidden="true"
                className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-current align-middle"
              />
            )}
          </div>
        )}

        {!content && isStreaming && (
          <span className="text-muted-foreground text-xs italic">
            Thinking…
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Renders a stored ChatMessage. Filters out tool/system roles.
 */
export function StoredMessage({ message }: { message: ChatMessage }) {
  if (message.role !== 'user' && message.role !== 'assistant') return null;
  return (
    <MessageBubble
      role={message.role}
      content={message.content}
      toolCalls={message.toolCalls}
    />
  );
}
